import { useState } from 'react';
import {
	EMPTY_MIXTURE_SETTINGS_FORM,
	type MixtureSettingsField,
	type MixtureSettingsForm,
} from '../constants/mixtures.constants';
import { MixtureSettingsSchema } from '../schemas/mixtures.schema';
import type { MaintenanceMixtureSettings } from '../types';

const MINUTES_PER_HOUR = 60;

const toHours = (minutes: string) => Number(minutes) / MINUTES_PER_HOUR;

const toMinutes = (hours: number) => String(Math.round(hours * MINUTES_PER_HOUR * 100) / 100);

const toSettingsForm = (settings?: MaintenanceMixtureSettings): MixtureSettingsForm =>
	settings
		? {
				vbox: String(settings.vbox),
				totalInjectionTime: toMinutes(settings.totalInjectionTime),
				totalOperationTime: toMinutes(settings.totalOperationTime),
				cemMax: String(settings.cemMax),
				desiredEventsPerMonth:
					settings.desiredEventsPerMonth != null ? String(settings.desiredEventsPerMonth) : '',
			}
		: EMPTY_MIXTURE_SETTINGS_FORM;

const toSettings = (form: MixtureSettingsForm): MaintenanceMixtureSettings => ({
	vbox: Number(form.vbox),
	totalInjectionTime: toHours(form.totalInjectionTime),
	totalOperationTime: toHours(form.totalOperationTime),
	cemMax: Number(form.cemMax),
	...(form.desiredEventsPerMonth
		? { desiredEventsPerMonth: Number(form.desiredEventsPerMonth) }
		: {}),
});

export const useMixtureSettingsForm = (savedSettings?: MaintenanceMixtureSettings) => {
	const [form, setForm] = useState(() => toSettingsForm(savedSettings));
	const [errors, setErrors] = useState<Record<string, string>>({});

	const isChanged = JSON.stringify(form) !== JSON.stringify(toSettingsForm(savedSettings));

	const set = (field: MixtureSettingsField, value: string) => {
		setForm((current) => ({ ...current, [field]: value }));
		setErrors((current) => ({ ...current, [field]: '' }));
	};

	const validate = (): MaintenanceMixtureSettings | null => {
		const parsed = MixtureSettingsSchema.safeParse(form);
		if (parsed.success) return toSettings(form);
		const nextErrors: Record<string, string> = {};
		parsed.error.issues.forEach((issue) => {
			nextErrors[String(issue.path[0])] = issue.message;
		});
		setErrors(nextErrors);
		return null;
	};

	return { form, errors, isChanged, set, validate };
};
